/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Switch } from '@components/atoms'
import {
	GridToolbarContainer,
	GridSlotProps,
	DEFAULT_GRID_AUTOSIZE_OPTIONS,
} from '@mui/x-data-grid'
import XLSX from 'xlsx-js-style'
import { mono, boFont, boSize } from '@components/systems/backoffice/backoffice.tokens'

import { useAppSelector } from '@redux/index'

type PropsType = GridSlotProps['toolbar'] & {
	onAddClick?: () => void
	toolbarVariant?: 'full' | 'search'
}

/**
 * 그리드 툴바 — 좌: 수정 모드 토글 · 검색(밑줄 입력) · 추가(고스트). 우: 텍스트 동작(엑셀·인쇄·간격).
 * 그라데이션 버튼 3색은 걷어냈다 — 위계는 색이 아니라 solid/ghost/text 세 단계로만(DESIGN_SYSTEM §5).
 */
export const GridToolBar = (props: PropsType) => {
	const { isLoading, apiRef, title, editModeOn, onEditModeToggle, hasActionColumn } =
		props
	// 'search' 면 검색 입력만 — 고객 화면에 수정·엑셀·인쇄를 내놓지 않는다
	const isSearchOnly = props.toolbarVariant === 'search'

	const userLanguage: string = useAppSelector(state => state.user.language)

	const onActionToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
		onEditModeToggle(e.target.checked)
	}

	// 검색 인풋 — 엔터 시 DataGrid quickFilter 로 모든 가시 컬럼 contains 검색.
	const onSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key !== 'Enter') return
		const v = String(e.currentTarget.value ?? '').trim()
		const words = v ? v.split(/\s+/).filter(Boolean) : []
		try {
			apiRef.current?.setQuickFilterValues(words)
		} catch {
			/* noop */
		}
	}

	const addTitle = userLanguage === 'ko' ? '추가' : 'Add'
	const exportTitle = userLanguage === 'ko' ? '엑셀 다운로드' : 'Excel download'
	const uploadTitle = userLanguage === 'ko' ? '엑셀 업로드' : 'Excel upload'
	const printTitle = userLanguage === 'ko' ? '인쇄' : 'Print'
	const autoSizeColumnsTitle = userLanguage === 'ko' ? '간격맞춤' : 'Autosize columns'
	const editModeTitle = userLanguage === 'ko' ? '수정 모드' : 'Edit mode'

	const onUploadClick = () => {
		if (typeof props.onExcelUploadClick === 'function') {
			props.onExcelUploadClick()
		}
	}
	const includeHeaders = DEFAULT_GRID_AUTOSIZE_OPTIONS.includeHeaders
	const includeOutliers = DEFAULT_GRID_AUTOSIZE_OPTIONS.includeOutliers
	const outliersFactor = String(DEFAULT_GRID_AUTOSIZE_OPTIONS.outliersFactor)
	const expand = DEFAULT_GRID_AUTOSIZE_OPTIONS.expand

	const onAddClick = () => {
		if (typeof props.onAddClick === 'function') {
			props.onAddClick()
		}
	}

	const autosizeOptions = {
		includeHeaders,
		includeOutliers,
		outliersFactor: Number.isNaN(parseFloat(outliersFactor))
			? 1
			: parseFloat(outliersFactor),
		expand,
	}

	const onExportClick = () => {
		const api: any = apiRef.current
		if (!api) return

		const allCols: any[] = api.getAllColumns?.() ?? []
		const visibleCols = allCols.filter(
			c =>
				c.field !== 'idx' &&
				c.field !== 'actions' &&
				c.field !== '__check__' &&
				!c.disableExport &&
				api.getColumn?.(c.field),
		)
		const visibilityModel = api.state?.columns?.columnVisibilityModel ?? {}
		const finalCols = visibleCols.filter(c => visibilityModel[c.field] !== false)

		const rowIds: any[] =
			api.getSortedRowIds?.() ?? Array.from((api.getRowModels?.() ?? new Map()).keys())

		const headers = finalCols.map((c: any) => c.headerName ?? c.field)
		const colWidths = headers.map((h: string) => h.length)

		const sheetData: any[][] = [headers]
		const cellStyles: Record<string, any> = {}

		const hdrFill = { fgColor: { rgb: '16181B' } }
		const hdrFont = { bold: true, color: { rgb: 'FFFFFF' }, sz: 11 }
		headers.forEach((_: string, ci: number) => {
			const addr = XLSX.utils.encode_cell({ r: 0, c: ci })
			cellStyles[addr] = {
				fill: hdrFill,
				font: hdrFont,
				alignment: { horizontal: 'center', vertical: 'center' },
			}
		})

		rowIds.forEach((id: any, ri: number) => {
			const row = api.getRow(id)
			const rowData: any[] = []
			finalCols.forEach((col: any, ci: number) => {
				let value: any = row?.[col.field]
				let style: any = null
				if (typeof col.exportCell === 'function') {
					const r = col.exportCell(value, row)
					value = r?.v ?? value
					style = r?.s ?? null
				} else {
					if (typeof col.valueGetter === 'function') {
						value = col.valueGetter(value, row, col, { current: api })
					}
					if (typeof col.valueFormatter === 'function') {
						value = col.valueFormatter(value, row, col, { current: api })
					}
				}
				if (value instanceof Date) value = value.toISOString()
				const str = value == null ? '' : String(value)
				if (str.length > colWidths[ci]) colWidths[ci] = str.length
				rowData.push(value)
				if (style) {
					const addr = XLSX.utils.encode_cell({ r: ri + 1, c: ci })
					cellStyles[addr] = style
				}
			})
			sheetData.push(rowData)
		})

		const ws = XLSX.utils.aoa_to_sheet(sheetData)
		ws['!cols'] = colWidths.map((w: number) => ({ wch: Math.max(w + 3, 8) }))
		for (const [addr, s] of Object.entries(cellStyles)) {
			if (ws[addr]) ws[addr].s = s
		}

		const wb = XLSX.utils.book_new()
		XLSX.utils.book_append_sheet(wb, ws, 'Sheet1')
		const dateStr = new Date().toISOString().slice(0, 10)
		const fileName = title ? `${title} ${dateStr}.xlsx` : `export ${dateStr}.xlsx`
		XLSX.writeFile(wb, fileName)
	}

	const onPrintClick = () => {
		apiRef.current.exportDataAsPrint({
			hideToolbar: true,
			hideFooter: true,
		})
	}

	return (
		<GridToolbarContainer css={{ padding: 0 }}>
			<View css={style}>
				{!isLoading && (
					<>
						<View className="leftToolView">
							{hasActionColumn && !isSearchOnly && (
								<label className={`actionToggle${editModeOn ? ' on' : ''}`}>
									<span className="actionToggleLabel">{editModeTitle}</span>
									<Switch size="small" checked={editModeOn} onChange={onActionToggle} />
								</label>
							)}
							<View className="gridSearchInput">
								<span className="mdi mdi-magnify searchIcon" aria-hidden="true" />
								<input
									type="text"
									aria-label="테이블 검색"
									placeholder={userLanguage === 'ko' ? '검색' : 'Search'}
									onKeyDown={onSearchKeyDown}
								/>
							</View>
							{editModeOn && !isSearchOnly && (
								<button type="button" className="ghostBtn" onClick={onAddClick}>
									<span className="mdi mdi-plus" aria-hidden="true" />
									{addTitle}
								</button>
							)}
							{props.leftToolbarSlot ? (
								<View className="leftToolbarSlot">{props.leftToolbarSlot}</View>
							) : null}
						</View>
						{!isSearchOnly && (
							<View className="rightToolGroup">
								<button type="button" className="textBtn" onClick={onExportClick}>
									{exportTitle}
								</button>
								{/* 엑셀 업로드는 페이지가 핸들러를 넘긴 그리드에서만 노출 */}
								{typeof props.onExcelUploadClick === 'function' && (
									<button type="button" className="textBtn" onClick={onUploadClick}>
										{uploadTitle}
									</button>
								)}
								<button type="button" className="textBtn" onClick={onPrintClick}>
									{printTitle}
								</button>
								<button
									type="button"
									className="textBtn"
									onClick={() => apiRef.current.autosizeColumns(autosizeOptions)}
								>
									{autoSizeColumnsTitle}
								</button>
							</View>
						)}
					</>
				)}
			</View>
		</GridToolbarContainer>
	)
}

const style = css({
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
	width: '100%',
	minHeight: 48,
	padding: '0 8px 0 4px',
	borderBottom: `1px solid ${mono.line}`,
	gap: 12,
	fontFamily: boFont.ui,
	background: mono.surface,

	'.leftToolView': {
		display: 'flex',
		alignItems: 'center',
		gap: 4,
		minWidth: 0,
	},

	'.rightToolGroup': {
		display: 'flex',
		alignItems: 'center',
		gap: 4,
		flexShrink: 0,
	},

	'.actionToggle': {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 2,
		height: boSize.ctlH,
		paddingLeft: 8,
		cursor: 'pointer',
		userSelect: 'none',

		'.actionToggleLabel': {
			fontSize: 11,
			fontWeight: 500,
			letterSpacing: '.16em',
			textTransform: 'uppercase',
			color: mono.faint,
			transition: 'color 200ms ease-out',
		},
		'&.on .actionToggleLabel': { color: mono.ink },
	},

	'.gridSearchInput': {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 6,
		height: boSize.ctlH,
		marginLeft: 8,
		width: 180,
		borderBottom: `1px solid ${mono.lineInput}`,
		transition: 'border-color 200ms ease-out, width 200ms ease-out',

		'&:focus-within': { borderBottomColor: mono.ink, width: 260 },

		'.searchIcon': { fontSize: 16, color: mono.faint, flexShrink: 0 },

		input: {
			flex: 1,
			minWidth: 0,
			border: 'none',
			outline: 'none',
			background: 'transparent',
			fontFamily: boFont.ui,
			fontSize: 13,
			fontWeight: 300,
			color: mono.ink,
			padding: 0,
			'&::placeholder': { color: mono.faint },
		},
	},

	'.ghostBtn': {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 4,
		height: 30,
		marginLeft: 12,
		padding: '0 12px 0 8px',
		fontFamily: boFont.ui,
		fontSize: 12,
		fontWeight: 500,
		color: mono.ink,
		background: 'transparent',
		border: `1px solid ${mono.ink}`,
		borderRadius: 2,
		cursor: 'pointer',
		transition: 'color 200ms ease-out, background-color 200ms ease-out',
		'.mdi': { fontSize: 15 },
		'&:hover': { background: mono.ink, color: '#FFFFFF' },
	},

	'.textBtn': {
		height: 30,
		padding: '0 8px',
		fontFamily: boFont.ui,
		fontSize: 12,
		fontWeight: 500,
		color: mono.dim,
		background: 'transparent',
		border: 0,
		borderRadius: 2,
		cursor: 'pointer',
		whiteSpace: 'nowrap',
		transition: 'color 200ms ease-out',
		'&:hover': { color: mono.ink },
	},

	'.leftToolbarSlot': {
		display: 'flex',
		alignItems: 'center',
		marginLeft: 12,
	},
})
